import React, {Component} from 'react'
import propTypes from 'prop-types'
import ReactDOM from 'react-dom'
import {isNil, get} from 'lodash'
import ColumnHeader, {MinColumnSize} from './columnHeader'
import Draggable from '../../../components/draggable'
import DragLayer from '../../../components/dragLayer'
import {prefix} from 'helpers/component'
import GroupOverlay from "components/pivotTable/components/groupOverlay"
import GroupHeader from "./groupHeader"
import {getGroupColumnLabel} from "helpers/pivotTable"

const getClassName = prefix('pivotTable-groupHeaders-')

class GroupHeaders extends Component {
  constructor(props) {
    super(props)
    this.state = {
      dragIndex: null,
    }
  }

  setColumn = (col, index) => {
    if (isNil(this.columns)) this.columns = []
    this.columns[index] = col
  }

  columnDrag = (index, pos) => {
    const {columns, controlColumns} = this.props

    let dragPos = 0
    let dragIndex = 0
    if (controlColumns)
      controlColumns.forEach(controlColumn => dragPos += get(controlColumn, 'size', 0))

    for (let i = 0; i < columns.length; i++) {
      const col = this.columns[i]
      const node = ReactDOM.findDOMNode(col)
      const size = node.offsetWidth

      const midPoint = dragPos + (size / 2)
      if (midPoint > pos.cursor.x) break

      dragPos += size
      dragIndex++
    }

    this.setState({dragIndex})
  }

  columnDrop = (index) => {
    const {dragIndex} = this.state
    const {onMove} = this.props
    let newIndex = dragIndex
    if (newIndex != null) {
      if (newIndex > index) newIndex--
      if (newIndex !== index) onMove(index, newIndex)
    }
    this.setState({dragIndex: null})
  }

  render() {
    const {
      columns,
      controlColumns,
      columnSizes,
      columnResizingAllowed,
      columnReorderAllowed,
      stickyTop,
      onMove,
      onSize,
      actionColumns,
      groupData,
      preLabel,
      label,
      postLabel,
      showSelector,
      expanded,
      onToggle,
      visibleTotalGroupFields,
    } = this.props
    const {dragIndex} = this.state

    let content = columns.map((column, index) => {
      let size = columnSizes[column.key]
      if (size == null)
        size = get(column, 'size', 0)

      if (size == null)
        size = 100

      if (size < MinColumnSize)
        size = MinColumnSize

      const groupColumnLabel = getGroupColumnLabel(column, groupData, visibleTotalGroupFields)
      const columnKey = get(column, 'key', null)
      const columnClassName = get(column, 'className', '')
      const columnAlign = get(column, 'align', null)

      return (

          <Draggable
              key={columnKey}
              ref={e => {
                this.setColumn(e, index)
              }}
              axis="x"
              active={columnReorderAllowed}
              onDrag={pos => this.columnDrag(index, pos)}
              onDrop={pos => this.columnDrop(index, pos)}
              style={{width: size}}
              className={`${getClassName('column')} ${columnClassName || ''}`}>
            <GroupOverlay
                preLabel={preLabel}
                label={label}
                postLabel={postLabel}
                showSelector={showSelector}
                onToggle={onToggle}
                expanded={expanded}
            />
            <GroupHeader
                id={columnKey}
                label={groupColumnLabel}
                align={columnAlign}
                size={size}
                canSize={columnResizingAllowed}
                onSize={(size) => onSize(columnKey, size)}
                onMove={(from, to) => onMove(from, to)}
            />
          </Draggable>
      )
    })

    if (dragIndex !== null) {
      let dragPos = 0
      if (dragIndex > 0) dragPos = -3
      if (dragIndex === columns.length) dragPos = -6

      const positioner = (
          <div key="_positioner" className={getClassName('positionerContainer')}>
            <div className={getClassName('positioner')} style={{left: dragPos}}/>
          </div>
      )
      content.splice(dragIndex, 0, positioner)
    }

    if (controlColumns) {
      content = [
        ...controlColumns.map((controlColumn, index) => {
          const controlColumnLabel = get(controlColumn, 'label', '')
          const controlColumnAlign = get(controlColumn, 'align', null)
          const controlColumnSize = get(controlColumn, 'size', null)

          return (
              <div key={':sort_' + index} className={getClassName('controlColumn')}
                   style={{width: controlColumnSize}}>
                <ColumnHeader
                    label={controlColumnLabel}
                    align={controlColumnAlign || "center"}
                    size={controlColumnSize}
                    canSort={false}
                    canSize={false}
                />
              </div>
          )
        }),
        ...content,
      ]
    }

    if (actionColumns) {
      content = [
        ...content,
        ...actionColumns.map((actionColumn, index) => {
          const actionColumnLabel = get(actionColumns, 'label', '')
          const actionColumnAlign = get(actionColumns, 'align', null)
          const actionColumnSize = get(actionColumns, 'size', null)
          return (
              <div key={`:action_${index}`} className={getClassName('controlColumn')}
                   style={{width: actionColumnSize}}>
                <ColumnHeader
                    label={actionColumnLabel}
                    align={actionColumnAlign || "center"}
                    size={actionColumnSize}
                    canSort={false}
                    canSize={false}
                />
              </div>
          )
        }),
      ]
    }

    let containerStyle
    const containerClasses = ['container']

    if (!isNil(stickyTop)) {
      containerClasses.push('container-sticky')
      containerStyle = {top: stickyTop}
    }

    return (
        <DragLayer style={containerStyle} className={getClassName(containerClasses)}>
          <div className={getClassName('root')}>
            {content}
          </div>
        </DragLayer>
    )
  }
}

GroupHeaders.propTypes = {
  columns: propTypes.arrayOf(propTypes.object).isRequired,
  columnSizes: propTypes.object.isRequired,
  columnReorderAllowed: propTypes.bool.isRequired,
  columnResizingAllowed: propTypes.bool.isRequired,
  fullSize: propTypes.bool.isRequired,
  stickyTop: propTypes.number,
  groupOffset: propTypes.number,
  controlColumns: propTypes.arrayOf(propTypes.shape({
    size: propTypes.number.isRequired,
    label: ColumnHeader.propTypes.label,
  })),
  actionColumns: propTypes.arrayOf(propTypes.shape({
    size: propTypes.number.isRequired,
    label: ColumnHeader.propTypes.label,
  })),
  onSize: propTypes.func.isRequired,
  onMove: propTypes.func.isRequired,
  groupData: propTypes.object.isRequired,
  preLabel: propTypes.element,
  label: ColumnHeader.propTypes.label,
  postLabel: propTypes.element,
  showSelector: propTypes.bool.isRequired,
  expanded: propTypes.bool.isRequired,
  onToggle: propTypes.func,
  visibleTotalGroupFields: propTypes.array,
}

GroupHeaders.defaultProps = {
  groupOffset: 0,
  columnResizingAllowed: true,
  visibleTotalGroupFields: [],
}

export default GroupHeaders
